import { NotificationsPage } from "./pages/NotificationsPage";
import { Navigate, type RouteObject } from "react-router";
import { AuthCallbackScreen } from "visumo-data-agent-web-shared";

import { AppShell } from "./AppShell";
import { AiConnectionsPage } from "./pages/AiConnectionsPage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { AioPage } from "./pages/AioPage";
import { AuditPage } from "./pages/AuditPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AuthenticatedArea } from "./AuthenticatedArea";
import { DelegationHandoffPage } from "./pages/DelegationHandoffPage";
import { ProductsAndPostsPage } from "./pages/ProductsAndPostsPage";
import { HelpPage } from "./pages/HelpPage";
import { HomePage } from "./pages/HomePage";
import { IntakePage } from "./pages/IntakePage";
import { InvitationAcceptPage } from "./pages/InvitationAcceptPage";
import { KnowledgeCoveragePage } from "./pages/KnowledgeCoveragePage";
import { KnowledgeSchemaPage } from "./pages/KnowledgeSchemaPage";
import { KnowledgeSheetPage } from "./pages/KnowledgeSheetPage";
import { LoginPage } from "./pages/LoginPage";
import { PersonaCoveragePage } from "./pages/PersonaCoveragePage";
import { PersonaEditPage } from "./pages/PersonaEditPage";
import { PersonaNewPage } from "./pages/PersonaNewPage";
import { PersonasPage } from "./pages/PersonasPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { PublishPage } from "./pages/PublishPage";
import { RegulationBookPage } from "./pages/RegulationBookPage";
import { PostsPage } from "./pages/PostsPage";
import { ProductsPage } from "./pages/ProductsPage";
import { QuestionMapPage } from "./pages/QuestionMapPage";
import { RuleDetailPage } from "./pages/RuleDetailPage";
import { RulesPage } from "./pages/RulesPage";
import { SellingAnglesPage } from "./pages/SellingAnglesPage";
import { TenantSelectPage } from "./pages/TenantSelectPage";
import { VideoMetaDetailPage } from "./pages/VideoMetaDetailPage";
import { VideoMetaPage } from "./pages/VideoMetaPage";

/**
 * パス方式のルート定義。hash は使わない。未知のパスは配信側の SPA フォールバックが
 * index.html を返し、末尾の `*` がホームへ送る。
 *
 * ルーターの生成（createBrowserRouter）とは分けてある。テストは同じ定義を
 * メモリ上のルーターへ載せて、画面ごとの遷移を実際の経路で確かめる。
 */
export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  {
    path: "/auth/callback",
    element: <AuthCallbackScreen fallbackPath="/tenants" />,
  },
  // 代行の受け取り口。**認証の外側**に置く(委任トークンを受け取る前は
  // ログインしていないので、認証済みの区画に入れるとログイン画面へ飛ぶ)
  { path: "/delegation/start", element: <DelegationHandoffPage /> },
  // 招待の受け取り口。**認証の外側**に置く(招待された人は、まだこの会社の
  // 利用者ではないので、認証済みの区画に入れるとログイン画面へ飛ばされる)。
  // トークンは URL のフラグメント(`#token=…`)で受ける
  { path: "/invitations/accept", element: <InvitationAcceptPage /> },
  {
    element: <AuthenticatedArea />,
    children: [
      { path: "/tenants", element: <TenantSelectPage /> },
      {
        path: "/",
        element: <AppShell />,
        children: [
          { index: true, element: <ProductsAndPostsPage /> },
          { path: "home", element: <HomePage /> },
          { path: "aio", element: <AioPage /> },
          { path: "ai-connections", element: <AiConnectionsPage /> },
          { path: "api", element: <ApiKeysPage /> },
          { path: "products", element: <ProductsPage /> },
          { path: "posts", element: <PostsPage /> },
          { path: "products/:productId", element: <ProductDetailPage /> },
          { path: "knowledge/coverage", element: <KnowledgeCoveragePage /> },
          { path: "knowledge/schema", element: <KnowledgeSchemaPage /> },
          {
            path: "knowledge/products/:productId",
            element: <KnowledgeSheetPage />,
          },
          { path: "knowledge/selling", element: <SellingAnglesPage /> },
          { path: "knowledge/regulation", element: <RegulationBookPage /> },
          { path: "question-map", element: <QuestionMapPage /> },
          { path: "publish", element: <PublishPage /> },
          { path: "intake", element: <IntakePage /> },
          { path: "audit", element: <AuditPage /> },
          // ヘルプはドロワーから「別のタブで開く」で来る。`*` はスラッグの
          // 残り全部（`knowledge/sheet` のように階層を持つ）
          { path: "help", element: <HelpPage /> },
          { path: "help/*", element: <HelpPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "notifications", element: <NotificationsPage /> },
          { path: "video-meta", element: <VideoMetaPage /> },
          { path: "video-meta/:videoId", element: <VideoMetaDetailPage /> },
          { path: "rules", element: <RulesPage /> },
          { path: "rules/:ruleId", element: <RuleDetailPage /> },
          { path: "personas", element: <PersonasPage /> },
          // 一覧より先に書く。":personaId" に "coverage" が吸われないようにする
          { path: "personas/coverage", element: <PersonaCoveragePage /> },
          { path: "personas/new", element: <PersonaNewPage /> },
          { path: "personas/:personaId", element: <PersonaEditPage /> },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
];
